import React, { useState, useMemo } from 'react';
import { useFetchCampaigns } from '../../../../hooks';
import { CopyQuestionsDialog, useCopyQuestions, type CopyResult } from '../../CopyQuestionsDialog';
import type { CampaignQuestion } from '../types';

interface CopyQuestionsManagerProps {
    isOpen: boolean;
    onClose: () => void;
    campaignId: number | null;
    initialQuestions: CampaignQuestion[];
}

/**
 * Handles copying questions to other campaigns.
 */
const CopyQuestionsManager: React.FC<CopyQuestionsManagerProps> = (props) => {
    const { isOpen, onClose, campaignId, initialQuestions } = props;
    const [copyResults, setCopyResults] = useState<CopyResult[] | null>(null);

    // @TODO: Replace with new hook that will fetch all campaigns with pagination
    const campaignsQuery = useFetchCampaigns({
        sorting: [],
        pagination: { pageIndex: 0, pageSize: 5000 },
        enabled: isOpen, // Fetch campaigns only when dialog is open
    });

    const campaigns = useMemo(() => campaignsQuery.data?.data || [], [campaignsQuery.data]);
    const copyQuestionsMutation = useCopyQuestions();

    const handleClose = () => {
        setCopyResults(null);
        onClose();
    };

    const handleCopyQuestions = (targetCampaignIds: number[]) => {
        if (!campaignId) return;

        copyQuestionsMutation.mutate(
            {
                questions: initialQuestions,
                targetCampaignIds,
                sourceCampaignId: campaignId,
            },
            {
                onSuccess: (data) => {
                    setCopyResults(data.results);
                },
            }
        );
    };

    if (!isOpen) return null;

    return (
        <CopyQuestionsDialog
            open={isOpen}
            onClose={handleClose}
            onCopy={handleCopyQuestions}
            campaigns={campaigns}
            isLoading={campaignsQuery.isLoading}
            isCopying={copyQuestionsMutation.isPending}
            currentCampaignId={campaignId}
            copyResults={copyResults}
        />
    );
};

export default CopyQuestionsManager;
